// DEPLOY: /var/www/woody.wtf/frontend/src/chart/AnalystMenu.js
// Chart 2 analyst ratings button: the icon turns rating dates on the chart on / off, the ▾ beside it
// opens the look settings (colour per rating, dot size / fade, line show / style / thickness / fade).
// Settings live in the user's chart prefs (analystStyle); ChartEngine.drawAnalyst reads them.
// Desktop popover under the button (as SetsMenu), phone bottom sheet.
import React, { useState, useEffect, useRef } from 'react';
import { IconVLine } from './icons';

export const ANALYST_DEFAULT = {
  colours: { Buy: '#16A34A', Hold: '#6B7280', Sell: '#DC2626' },
  dotSize: 3, dotAlpha: 0.85,
  line: true, lineStyle: 'dotted', lineWidth: 1, lineAlpha: 0.35,
};
export const ANALYST_DASH = { solid: [], dashed: [6, 4], dotted: [2, 3] };

// Stored prefs over the defaults (colours merged so a new rating colour still gets its default).
export const analystStyle = stored => ({ ...ANALYST_DEFAULT, ...(stored || {}),
  colours: { ...ANALYST_DEFAULT.colours, ...(stored?.colours || {}) } });

const S = {
  wrap:   { position: 'relative', flexShrink: 0 },
  grp:    { display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' },
  btn:    { display: 'flex', alignItems: 'center', padding: '5px 8px', border: 'none', background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer' },
  on:     { background: 'var(--accent)', color: '#fff' },
  caret:  { padding: '5px 6px', border: 'none', borderLeft: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '10px' },
  menu:   { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 120, width: '260px', padding: '10px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' },
  shade:  { position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.35)' },
  sheet:  { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 301, maxHeight: '75vh', overflowY: 'auto', background: 'var(--surface)', borderRadius: '14px 14px 0 0', padding: '12px 16px calc(16px + env(safe-area-inset-bottom))', boxShadow: 'var(--shadow)' },
  head:   { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '8px 0 4px' },
  row:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '4px 0', fontSize: '12px', color: 'var(--text)' },
  colour: { width: '36px', height: '26px', padding: 0, border: '1px solid var(--border)', borderRadius: '6px', background: 'none', cursor: 'pointer' },
  range:  { flex: 1, maxWidth: '130px', accentColor: 'var(--accent)' },
  val:    { width: '34px', textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '11px', color: 'var(--text-muted)' },
  check:  { width: '18px', height: '18px', accentColor: 'var(--accent)', margin: 0 },
  seg:    { display: 'flex', border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' },
  segBtn: { border: 'none', borderRight: '1px solid var(--border)', padding: '4px 8px', fontSize: '11px', background: 'var(--bg)', color: 'var(--text-muted)', cursor: 'pointer' },
  reset:  { width: '100%', marginTop: '10px', padding: '7px', border: '1px dashed var(--border)', borderRadius: '4px', background: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '12px' },
};

export default function AnalystMenu({ on, onToggle, style, onStyle, mobile }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open || mobile) return;
    const close = e => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open, mobile]);

  const slider = (label, key, min, max, step, fmt) => (
    <div style={S.row}>
      <span>{label}</span>
      <input type="range" style={S.range} min={min} max={max} step={step} value={style[key]}
             onChange={e => onStyle({ [key]: parseFloat(e.target.value) })} />
      <span style={S.val}>{fmt(style[key])}</span>
    </div>
  );
  const pct = v => `${Math.round(v * 100)}%`, px = v => `${v}px`;

  const body = (
    <>
      <div style={{ ...S.head, marginTop: 0 }}>Colours</div>
      {Object.keys(ANALYST_DEFAULT.colours).map(k => (
        <div key={k} style={S.row}>
          <span>{k}</span>
          <input type="color" style={S.colour} value={style.colours[k]}
                 onChange={e => onStyle({ colours: { ...style.colours, [k]: e.target.value } })} />
        </div>
      ))}
      <div style={S.head}>Dot</div>
      {slider('Size', 'dotSize', 2, 10, 1, px)}
      {slider('Fade', 'dotAlpha', 0.05, 1, 0.05, pct)}
      <div style={S.head}>Line</div>
      <div style={S.row}>
        <span>Show</span>
        <input type="checkbox" style={S.check} checked={style.line} onChange={e => onStyle({ line: e.target.checked })} />
      </div>
      <div style={{ ...S.row, ...(style.line ? {} : { opacity: 0.4, pointerEvents: 'none' }) }}>
        <span>Style</span>
        <div style={S.seg}>
          {Object.keys(ANALYST_DASH).map((k, i, a) => (
            <button key={k} type="button" onClick={() => onStyle({ lineStyle: k })}
                    style={{ ...S.segBtn, ...(i === a.length - 1 ? { borderRight: 'none' } : {}), ...(style.lineStyle === k ? S.on : {}) }}>
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div style={style.line ? {} : { opacity: 0.4, pointerEvents: 'none' }}>
        {slider('Thickness', 'lineWidth', 1, 3, 1, px)}
        {slider('Fade', 'lineAlpha', 0.05, 1, 0.05, pct)}
      </div>
      <button type="button" style={S.reset} onClick={() => onStyle(ANALYST_DEFAULT, true)}>Reset to defaults</button>
    </>
  );

  return (
    <div ref={boxRef} style={S.wrap}>
      <div style={S.grp}>
        <button type="button" style={{ ...S.btn, ...(on ? S.on : {}) }} onClick={onToggle}
                title={`Analyst rating dates on the chart: ${on ? 'On' : 'Off'}`}>
          <IconVLine size={14} />
        </button>
        <button type="button" style={S.caret} onClick={() => setOpen(o => !o)} title="Analyst ratings look">▾</button>
      </div>
      {open && (mobile
        ? <><div style={S.shade} onClick={() => setOpen(false)} /><div style={S.sheet}>{body}</div></>
        : <div style={S.menu}>{body}</div>)}
    </div>
  );
}
